import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AuthService, RoutingService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService, CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { TranslationService } from '../../../../core/i18n';
import { AddonGroup, AvailableSoldTosSearchParams, Category, CmsNavigationComponent, SoldTo } from '../../../../core/model';
import { RoutingHelperService } from '../../../../core/routing';
import { PrincipalConfigurationService } from '../../../../core/user';
import { shallowEqualObjects } from '../../../../core/util';
import { DrawerService, DrawerWidth } from '../../../../shared';
import { infinitelyScrolling } from '../../../../shared/utils/infinitely-scrolling';
import { SoldToFacade } from '../../../sold-to-base';
import { MobileNavigationStateService, State } from '../../services/mobile-navigation-state.service';
import { NavigationService } from '../../services/navigation.service';
import { NavigationNode } from '../navigation/navigation-node.model';

@Component({
  selector: 'py-header-mobile-navigation',
  templateUrl: './mobile-navigation.component.html',
  styleUrls: ['./mobile-navigation.component.scss'],
  standalone: false,
})
export class MobileNavigationComponent implements OnInit, OnDestroy {
  @ViewChild('menuDrawerTemplate', { read: TemplateRef }) menuDrawerTemplateRef: TemplateRef<any>;
  @ViewChild('drawerTemplate', { read: ElementRef }) drawerTemplate: ElementRef<HTMLDivElement>;

  public get state(): typeof State {
    return State;
  }

  activeSubId: string;
  mya: boolean;

  myPageLinks: Array<CmsNavigationComponent> = [];
  subItems: Array<NavigationNode> = [];
  navigationNode: Observable<NavigationNode>;
  isLoggedIn$: Observable<boolean> = this.authService.isUserLoggedIn();
  searchBoxVisible$: Observable<boolean>;
  mobileVisible$: Observable<boolean>;

  categories$: Observable<Category[]>;
  availableSoldTos$: Observable<Array<SoldTo>>;
  activeSoldTo$: Observable<SoldTo>;
  soldTosLoading$: Observable<Boolean>;
  currentPage$: Observable<number>;

  activeAreaOfUseCategory: Category;

  form = new UntypedFormGroup({
    search: new UntypedFormControl(''),
  });

  private subscriptions = new Subscription();

  selectedIndex = 0;

  constructor(
    private componentData: CmsComponentData<CmsNavigationComponent>,
    private navigationService: NavigationService,
    protected authService: AuthService,
    private drawerService: DrawerService,
    private mobileNavigationStateService: MobileNavigationStateService,
    private soldToService: SoldToFacade,
    private translationService: TranslationService,
    private routingService: RoutingService,
    private routingHelperService: RoutingHelperService,
    private principalConfigurationService: PrincipalConfigurationService,
    private breakpointService: BreakpointService
  ) {}

  ngOnInit() {
    this.navigationNode = this.navigationService.createNavigation(this.componentData.data$);
    this.activeSoldTo$ = this.soldToService.getActiveSoldTo();
    this.soldTosLoading$ = this.soldToService.getAvailableSoldTosLoading();
    this.searchBoxVisible$ = this.routingHelperService.searchBoxVisible();
    this.mobileVisible$ = this.breakpointService.isDown(BREAKPOINT.sm);

    const searchParametersForAvailableSoldTos$: Observable<AvailableSoldTosSearchParams> = this.soldToService
      .getSearchParametersForAvailableSoldTos()
      .pipe(distinctUntilChanged(shallowEqualObjects), shareReplay({ bufferSize: 1, refCount: true }));

    this.currentPage$ = searchParametersForAvailableSoldTos$.pipe(
      map((params) => params.page),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.availableSoldTos$ = infinitelyScrolling(this.soldToService.getAvailableSoldTos(), this.currentPage$, 'uid').pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscriptions.add(
      this.principalConfigurationService
        .require('enableMultipleCustomer')
        .pipe(switchMap((_) => searchParametersForAvailableSoldTos$))
        .subscribe(
          (searchParams) => {
            this.soldToService.searchForAvailableSoldTos(searchParams);
          },
          () => {}
        )
    );

    this.subscriptions.add(
      this.form
        .get('search')
        .valueChanges.pipe(debounceTime(400))
        .subscribe((value: string) => {
          this.triggerSearch(value);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleBackButtonClick() {
    const prevItem = this.mobileNavigationStateService.getPrevItems[this.mobileNavigationStateService.getPrevItems.length - 1];

    this.mobileNavigationStateService.setDrawerTitle(prevItem.title);
    this.mobileNavigationStateService.setCurrentState(prevItem.state);
    this.subItems = prevItem.subItems;
    this.activeAreaOfUseCategory = undefined;
    this.mobileNavigationStateService.setPrevItems(
      this.mobileNavigationStateService.getPrevItems.filter((prev) => prev !== prevItem)
    );
  }

  get currentState(): State {
    return this.mobileNavigationStateService.currentState;
  }

  get drawerTitle(): string {
    return this.mobileNavigationStateService.getDrawerTitle;
  }

  openMenu(): void {
    this.drawerService
      .open({
        template: this.menuDrawerTemplateRef,
        width: DrawerWidth.NearFull,
      })
      .pipe(take(1))
      .subscribe();
  }

  selectSoldTo(soldToId: string) {
    this.mobileNavigationStateService.setDrawerTitle('');
    this.mobileNavigationStateService.setCurrentState(State.Menu);
    this.subItems = [];
    this.soldToService.changeSoldTo(soldToId);
  }

  fetchMoreSoldTos() {
    this.soldToService
      .getAvailableSoldTosPagination()
      .pipe(
        take(1),
        withLatestFrom(this.currentPage$),
        filter(([pagination, currentPage]) => currentPage + 1 < pagination.totalPages),
        map(([_pagination, currentPage]) => currentPage + 1)
      )
      .subscribe((page) => {
        this.triggerSearch(this.form.get('search').value, page);
      });
  }

  handleCategoryCodeChange(data: { item: Category; mya: boolean }) {
    this.mobileNavigationStateService.addPrevItem({
      title: this.mobileNavigationStateService.getDrawerTitle,
      subItems: [],
      state: State.ProductsMenu,
    });
    this.activeSubId = data?.item?.code;
    this.mya = data?.mya;
    this.mobileNavigationStateService.setDrawerTitle(data?.item?.name);
    this.mobileNavigationStateService.setCurrentState(State.ProductsSubMenu);
    this.drawerTemplate?.nativeElement?.parentElement?.scrollTo(0, 0);
  }

  handleAddonGroupChange(addonGroup: AddonGroup) {
    this.mobileNavigationStateService.addPrevItem({
      title: this.mobileNavigationStateService.getDrawerTitle,
      subItems: [],
      state: State.ProductsMenu,
    });
    this.activeSubId = addonGroup?.id;
    this.mobileNavigationStateService.setDrawerTitle(addonGroup?.title);
    this.mobileNavigationStateService.setCurrentState(State.AddonSubMenu);
  }

  handleShowMyAssortmentSubMenuChange() {
    this.mobileNavigationStateService.addPrevItem({
      title: this.mobileNavigationStateService.getDrawerTitle,
      subItems: [],
      state: State.ProductsMenu,
    });

    this.translationService.translate('myAssortment.page_title').pipe(
      take(1),
      map((t) => this.mobileNavigationStateService.setDrawerTitle(t))
    );
    this.mobileNavigationStateService.setCurrentState(State.MyAssortmentProductsSubMenu);
  }

  handleShowCampaignsSubMenuChange() {
    this.mobileNavigationStateService.addPrevItem({
      title: this.mobileNavigationStateService.getDrawerTitle,
      subItems: [],
      state: State.ProductsMenu,
    });

    this.translationService.translate('navigation.campaigns_title').pipe(
      take(1),
      map((t) => this.mobileNavigationStateService.setDrawerTitle(t))
    );
    this.mobileNavigationStateService.setCurrentState(State.CampaignsSubMenu);
  }

  private triggerSearch(text?: string, page?: number) {
    const searchParams: AvailableSoldTosSearchParams = {
      key: text || 'latest',
      text,
      count: 25,
      page: page || 0,
    };
    this.soldToService.updateSearchParametersForAvailableSoldTos(searchParams);
  }

  setActiveAreaofUseCategory(category: Category): void {
    if (category?.code !== this.activeAreaOfUseCategory?.code) {
      this.mobileNavigationStateService.addPrevItem({
        title: this.mobileNavigationStateService.getDrawerTitle,
        subItems: [],
        state: State.AreaOfUseMenu,
      });
      this.activeAreaOfUseCategory = category;
      this.mobileNavigationStateService.setDrawerTitle(category?.name);
      this.mobileNavigationStateService.setCurrentState(State.AreaOfUseSubMenu);
      this.drawerTemplate?.nativeElement?.parentElement?.scrollTo(0, 0);
    }
  }

  goToDashboard(): void {
    this.routingService.go({ cxRoute: 'dashboard' });
  }

  goToLoginPage(): void {
    this.routingService.go({ cxRoute: 'login' });
  }
}
